import { SubstituteText } from '@/components/review/SubstituteText';
import type { FindingPreview } from '@/hooks/useSubstitutePreview';
import type { ExportMode, FindingModel } from '@/types/documents.types';
import { DEFAULT_COLORS } from '@/utils/canvasColors';
import { UNKNOWN_CATEGORY_COLOR } from '@/utils/categories';
import { findingItemName, findingMethod } from '@/utils/findings';
import { scaleRect } from '@/utils/geometry';
import { placeSubstitute } from '@/utils/substituteLayout';
import { type SubstituteLine, placeSubstituteLines } from '@/utils/substituteLines';
import { measureSubstitute } from '@/utils/textMeasure';
import type { CSSProperties, FC, KeyboardEvent } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** How far a highlight or black box reaches past the text rectangle, in CSS pixels. */
const PAD_X = 2;
const PAD_TOP = 1;
const PAD_BOTTOM = 3;

/**
 * How far the anonymized cover reaches past the glyphs, in CSS pixels. It is painted in the
 * paper's own colour, so it only has to hide antialiased glyph edges; any more would start eating
 * into the lines above and below.
 */
const PAD_COVER = 1;

/** Stands in for a substitute that has not reached the client, in the muted text colour. */
export const PLACEHOLDER = '•••';
const PLACEHOLDER_INK = '#8194a6';

interface FindingOverlayProps {
  finding: FindingModel;
  /** Colour of the finding's display category in the session snapshot; grey when unknown. */
  color: string | undefined;
  /** Pixels per PDF point at the current render size. */
  factor: number;
  mode: ExportMode;
  /** True when the finding takes part in the export (active and its category included). */
  inExport: boolean;
  selected: boolean;
  onSelect: (findingId: string) => void;
  /**
   * Fonts, baseline and colours read off the rendered page for the anonymized mode
   * (`useSubstitutePreview`); without them the box is placed from its rectangle alone.
   */
  preview?: FindingPreview;
}

/**
 * The boxes drawn over one finding, one per line it spans, in the three looks:
 *
 * - `original` — the category tint with a 2 px category underline; the tint is translucent here
 *   (the prototype's is opaque HTML behind text) so the PDF's own glyphs keep reading through;
 * - `redacted` — the opaque near-black box the exported file will carry;
 * - `anonymized` — what the exported file will show: the original glyphs painted out of the page
 *   picture (anything behind them, a watermark or a shaded cell, stays) and the substitute set on
 *   their baseline, at the export's size and ink, in its font or the closest installed twin
 *   (`SubstituteText`); a finding that wraps gets its substitute shared out over its lines, each
 *   part fitted to its own line (`utils/substituteLines`), as in the export. The preview's own
 *   addition is a 1 px dashed underline in the category colour that tells a reviewer a substitute
 *   from the document's own text. A declared line (`declaration` source) gets Lorem Ipsum like any other.
 *
 * A finding the export leaves alone is not drawn at all, as in the prototype. The selected
 * finding gets a 2 px ring in its colour, and becoming selected scrolls its first box into view.
 */
export const FindingOverlay: FC<FindingOverlayProps> = ({
  finding,
  color,
  factor,
  mode,
  inExport,
  selected,
  onSelect,
  preview,
}) => {
  const { t } = useTranslation();
  const firstBoxRef = useRef<HTMLButtonElement>(null);
  const cat = color || UNKNOWN_CATEGORY_COLOR;

  // `nearest` leaves a box that is already on screen where it is; `scroll-my-12` stops it from
  // parking flush against the edge, where its last pixels would be cut off.
  useEffect(() => {
    if (selected) firstBoxRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);

  if (!inExport) return null;

  const title = `${findingItemName(finding) || findingMethod(finding, t)}: ${finding.text}`;
  const substituted = mode === 'anonymized';
  const blackedOut = mode === 'redacted';
  const ring: CSSProperties = selected ? { boxShadow: `0 0 0 1px ${cat}` } : {};

  // Every line's pieces of the substitute: from the preview, or placed from the rectangles alone
  // while the page has not been read. A missing substitute shows the placeholder on the first line.
  const lines: SubstituteLine[][] = !substituted
    ? []
    : finding.substitute
      ? (preview?.lines ??
        placeSubstituteLines(finding.substitute, finding.text, finding.rects, () => [], measureSubstitute))
      : finding.rects.map((rect, index) =>
          index === 0
            ? [{ text: PLACEHOLDER, placement: placeSubstitute(PLACEHOLDER, rect, null, measureSubstitute) }]
            : []
        );

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(finding.id);
    }
  };

  return (
    <>
      {finding.rects.map((rect, index) => {
        const pixels = scaleRect(rect, factor);
        let style: CSSProperties;
        // Without a preview (the page not read yet) the cover is the padded rectangle in white.
        const cover = substituted ? preview?.covers[index] : undefined;
        const coverBox = cover?.box ?? {
          left: pixels.left - PAD_COVER,
          top: pixels.top - PAD_COVER,
          width: pixels.width + 2 * PAD_COVER,
          height: pixels.height + 2 * PAD_COVER,
        };
        if (substituted) {
          style = {
            backgroundColor: cover?.paper ?? DEFAULT_COLORS.paper,
            // The page itself with the glyphs painted out, so a watermark behind them stays whole.
            ...(cover?.image
              ? { backgroundImage: `url("${cover.image}")`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' }
              : {}),
            left: `${coverBox.left}px`,
            top: `${coverBox.top}px`,
            width: `${coverBox.width}px`,
            height: `${coverBox.height}px`,
          };
        } else {
          // The prototype pads a mark by 2 px sideways; the line rectangles hug the glyphs, so the
          // box also grows a little downwards, which puts the underline below the descenders.
          style = {
            ...(blackedOut
              ? { background: '#151c23' }
              : { background: `color-mix(in srgb, ${cat} 20%, transparent)`, borderBottom: `2px solid ${cat}` }),
            left: `${pixels.left - PAD_X}px`,
            top: `${pixels.top - PAD_TOP}px`,
            width: `${pixels.width + 2 * PAD_X}px`,
            height: `${pixels.height + PAD_TOP + PAD_BOTTOM}px`,
            ...ring,
          };
        }
        const pieces = lines[index] ?? [];
        return (
          <button
            key={`${finding.id}-${index}`}
            ref={index === 0 ? firstBoxRef : undefined}
            type="button"
            style={style}
            title={title}
            aria-label={title}
            onClick={() => onSelect(finding.id)}
            onKeyDown={handleKeyDown}
            className={`pointer-events-auto absolute cursor-pointer scroll-my-12 border-none p-0 ${
              substituted ? 'overflow-visible' : 'overflow-hidden'
            }`}
          >
            {substituted ? (
              // Above every cover of the page, like the substitute: covers reach from ascent to
              // descent and overlap the line above, which would otherwise hide this line's mark.
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-10"
                style={{ borderBottom: `1px dashed ${cat}`, ...ring }}
              />
            ) : null}
            {pieces.map((piece, column) => (
              <SubstituteText
                // A line's pieces are its columns, in order; nothing reorders them.
                // biome-ignore lint/suspicious/noArrayIndexKey: the column is the piece's identity
                key={column}
                text={piece.text}
                placement={piece.placement}
                ink={finding.substitute ? (cover?.ink ?? DEFAULT_COLORS.ink) : PLACEHOLDER_INK}
                factor={factor}
                boxLeft={coverBox.left}
                boxTop={coverBox.top}
              />
            ))}
          </button>
        );
      })}
    </>
  );
};
